import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { CaretLeft, CaretRight, Sparkle, X } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';
import {
  Atualizacao,
  PREVIEW_LOCAL,
  STORAGE_AVISOS_VISTOS,
  avisosVisiveis,
  tempoLeitura,
} from '../atualizacoes';

/** Lê os ids já dispensados. Storage bloqueado ou corrompido nunca quebra a tela. */
function lerVistos(): string[] {
  // Em preview ignora o que já foi dispensado: senão o card some para sempre
  // depois da primeira passada e não há como revisar texto e visual de novo.
  // (E some de vez se o storage tiver ficado sujo de uma versão anterior.)
  if (PREVIEW_LOCAL) return [];
  try {
    const cru = localStorage.getItem(STORAGE_AVISOS_VISTOS);
    if (!cru) return [];
    const arr = JSON.parse(cru);
    return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function gravarVistos(ids: string[]): void {
  // Em preview não persiste: assim recarregar a página traz os avisos de volta e
  // dá para revisar texto e visual quantas vezes for preciso. Some junto com o
  // PREVIEW_LOCAL quando isto virar commit.
  if (PREVIEW_LOCAL) return;
  try {
    // Poda: guarda só os ids que ainda existem no código. Sem isso a lista cresce
    // para sempre com avisos que já saíram de circulação.
    const validos = new Set(avisosVisiveis().map(a => a.id));
    localStorage.setItem(STORAGE_AVISOS_VISTOS, JSON.stringify(ids.filter(id => validos.has(id))));
  } catch {
    // Navegador com storage bloqueado: o aviso reaparece na próxima visita.
    // É pior que o ideal, e melhor que a tela quebrar.
  }
}

/**
 * Card FLUTUANTE de "Melhorias recentes" do ContaCil.
 *
 * Montado no Layout, fora do `{children}` e condicionado à rota do módulo —
 * mesmo padrão da AuditoriaTopbar e da FinanceiroTopbar. É isso que faz ele
 * ACOMPANHAR a navegação: uma única instância, que não remonta ao trocar de tela
 * e por isso não reinicia o tempo de leitura a cada clique.
 *
 * Ancorado no canto inferior direito. Não é modal, não rouba foco e não bloqueia
 * nada: `position: fixed` sem overlay, sai sozinho no fim do tempo de leitura e
 * pode ser fechado a qualquer momento.
 *
 * Com mais de um aviso vira um carrossel enxuto (um por vez, com setas e
 * marcadores), em vez de despejar um texto longo — a lista inteira de uma vez é
 * o que faz o usuário ignorar o aviso.
 */
export function CardAtualizacoes() {
  // A lista é congelada na montagem, de propósito. Se ela reagisse ao que já foi
  // visto, encolheria embaixo do usuário — o total caía de 8 para 7 para 6 e o
  // aviso anterior deixava de existir, então não havia como voltar nele.
  //
  // Aqui entra a lista COMPLETA (não só a não vista): recolhido, o acionador
  // continua dando acesso ao histórico do que já foi lido. O que decide a
  // abertura automática é `temNovidade`, calculado uma vez abaixo.
  const [lista] = useState<Atualizacao[]>(() => avisosVisiveis());
  // Quantas o usuário ainda não viu — é o número que o acionador anuncia.
  const [naoLidos] = useState<number>(() => {
    const vistos = lerVistos();
    return avisosVisiveis().filter(a => !vistos.includes(a.id)).length;
  });
  const temNovidade = naoLidos > 0;

  const [idx, setIdx] = useState(0);
  // Abre sozinho SÓ quando há novidade não vista. Depois de dispensada, a
  // release não reabre a cada navegação — fica o acionador.
  const [aberto, setAberto] = useState<boolean>(temNovidade);
  // Mexeu nas setas: o usuário assumiu o controle e o rodízio automático para.
  // Sem isso, ele volta um aviso e o relógio o joga para frente de novo.
  const [pausado, setPausado] = useState(false);

  const atual = lista[idx];
  const total = lista.length;

  // Recolher NÃO some com o componente: vira o acionador abaixo do avatar, que
  // é o que mantém as novidades acessíveis no mesmo lugar. E marca como visto —
  // dispensar é o gesto de "já vi isto".
  const recolher = useCallback(() => {
    setAberto(false);
    gravarVistos(lista.map(a => a.id));
  }, [lista]);

  const irPara = useCallback((i: number) => {
    setPausado(true);
    setIdx(i);
  }, []);

  // Rodízio automático: AVANÇA, não dispensa. Ao chegar no fim da leva, recolhe
  // para o acionador e marca tudo como visto — que é a única hora em que "já vi
  // isto" é verdade. Só roda com o card aberto.
  useEffect(() => {
    if (!atual || pausado || !aberto) return undefined;
    const t = setTimeout(() => {
      if (idx < total - 1) setIdx(i => i + 1);
      else recolher();
    }, tempoLeitura(atual.texto));
    return () => clearTimeout(t);
  }, [atual, idx, total, pausado, aberto, recolher]);

  if (!atual) return null;

  const rotulo = total > 1 ? `${idx + 1} de ${total} melhorias recentes` : 'Melhoria recente';
  // Novidade ainda não lida E card recolhido: é o que faz o acionador chamar.
  const chamando = temNovidade && !aberto;

  return (
    <Box
      position="fixed"
      // Ancorado ao HEADER, não ao conteúdo: o header é `fixed` com 64px de
      // altura, então 72px deixa 8px de respiro e o card nasce logo abaixo do
      // avatar. Preso ao conteúdo, ele sairia da tela ao rolar a página.
      top="72px"
      // Alinhado pela direita, na mesma margem do header (`pr-6` = 24px), para
      // o card ficar aprumado com o avatar e nunca vazar a lateral.
      right={{ base: 3, md: '24px' }}
      left={{ base: 3, md: 'auto' }}
      maxW={{ base: 'none', md: '400px' }}
      // Acima do conteúdo e do header (z-50), e ABAIXO do popover do avatar e de
      // modais: abrir o menu do usuário tem que cobrir o card, não competir com
      // ele. Também abaixo do menu portalizado de select (2000).
      zIndex={900}
      pointerEvents="auto"
      // Cresce e recolhe na direção do avatar, que é o canto de origem.
      sx={{ transformOrigin: 'top right' }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!aberto ? (
          // ── Recolhido: acionador discreto abaixo do avatar ──────────────
          <motion.div
            key="acionador"
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Box position="relative">
              {/* Halo que expande e some, atrás do acionador. É o que faz o olho
                  ir até ali sem precisar mexer no botão em si. `pointerEvents
                  none` para não roubar o clique. */}
              {chamando && (
                <motion.span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    inset: -2,
                    borderRadius: 9999,
                    background: 'rgba(99, 102, 241, 0.35)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                  animate={{ scale: [1, 1.35, 1.5], opacity: [0.55, 0.2, 0] }}
                  transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.6 }}
                />
              )}

              <motion.div
                // O botão inteiro respira junto com o halo — leve, mas o
                // suficiente para ser notado na visão periférica.
                animate={chamando ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                transition={
                  chamando
                    ? { duration: 1.8, repeat: Infinity, repeatDelay: 0.6, ease: 'easeInOut' }
                    : { duration: 0.2 }
                }
                style={{ position: 'relative', zIndex: 1 }}
              >
                <Flex
                  as="button"
                  onClick={() => setAberto(true)}
                  aria-label={
                    chamando ? 'Ver as novidades ainda não lidas' : 'Ver as melhorias recentes'
                  }
                  align="center"
                  gap={2}
                  px={chamando ? 3 : 2}
                  py={1.5}
                  borderRadius="full"
                  // Chamando, o acionador se pinta: fundo sólido em vez de
                  // branco translúcido. Lido, volta a ser discreto.
                  bg={chamando ? '#6366F1' : 'rgba(255, 255, 255, 0.9)'}
                  borderWidth="1px"
                  borderColor={chamando ? '#6366F1' : 'rgba(15,23,42,0.08)'}
                  boxShadow={
                    chamando
                      ? '0 8px 22px -6px rgba(99,102,241,0.65)'
                      : '0 6px 18px -6px rgba(15,23,42,0.18)'
                  }
                  color={chamando ? 'white' : '#6366F1'}
                  fontSize="11px"
                  fontWeight="700"
                  _hover={{ filter: 'brightness(1.06)' }}
                >
                  <Sparkle size={15} weight="fill" />
                  {chamando && (
                    <>
                      <Box as="span">
                        {naoLidos > 1 ? `${naoLidos} novidades` : 'Nova melhoria'}
                      </Box>
                      <Box
                        as="span"
                        w="7px"
                        h="7px"
                        borderRadius="full"
                        bg="white"
                        boxShadow="0 0 0 3px rgba(255,255,255,0.35)"
                      />
                    </>
                  )}
                </Flex>
              </motion.div>
            </Box>
          </motion.div>
        ) : (
          // ── Aberto: o card, preservado como estava ──────────────────────
          <motion.div
            key="card"
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{ transformOrigin: 'top right' }}
          >
            {/* Bico apontando para o avatar: liga o card à área do usuário sem
                pesar. Alinhado à direita, na vertical do próprio avatar. */}
            <Box
              position="absolute"
              top="-6px"
              right="18px"
              w="12px"
              h="12px"
              bg="rgba(255, 255, 255, 0.92)"
              borderLeftWidth="1px"
              borderTopWidth="1px"
              borderColor="rgba(255, 255, 255, 0.6)"
              transform="rotate(45deg)"
              zIndex={1}
            />
            <GlassCard
              intensity="strong"
              p={4}
              role="status"
              aria-live="polite"
              boxShadow="0 1px 0 0 rgba(255,255,255,0.9) inset, 0 20px 60px -16px rgba(27,136,245,0.35), 0 8px 24px -8px rgba(15,23,42,0.16)"
            >
              <Flex align="flex-start" gap={3}>
                <Box color="#6366F1" flexShrink={0} mt="2px">
                  <Sparkle size={22} weight="duotone" />
                </Box>

                <Box minW={0} flex={1}>
                  <Flex align="center" gap={2} mb={1} wrap="wrap">
                    <Text
                      fontSize="10px"
                      fontWeight="700"
                      color="#6366F1"
                      textTransform="uppercase"
                      letterSpacing="0.08em"
                    >
                      {rotulo}
                    </Text>
                    {atual.areas.map(a => (
                      <Box
                        key={a}
                        as="span"
                        px={2}
                        borderRadius="full"
                        fontSize="10px"
                        fontWeight="600"
                        bg="rgba(99, 102, 241, 0.08)"
                        color="#6366F1"
                      >
                        {a}
                      </Box>
                    ))}
                  </Flex>

                  {/* mode="wait" evita os dois textos empilhados no meio da troca */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={atual.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <Text fontSize="sm" fontWeight="600" color="#0F172A">
                        {atual.titulo}
                      </Text>
                      <Text fontSize="xs" color="#64748B" mt="2px">
                        {atual.texto}
                      </Text>
                    </motion.div>
                  </AnimatePresence>

                  {total > 1 && (
                    <Flex align="center" gap={1} mt={2}>
                      {/* Circular nas duas pontas: com 8 avisos, obrigar a voltar
                        um a um até o começo é pior que dar a volta. */}
                      <IconButton
                        aria-label="Melhoria anterior"
                        icon={<CaretLeft size={12} weight="bold" />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        onClick={() => irPara((idx - 1 + total) % total)}
                      />
                      {lista.map((a, i) => (
                        <Box
                          key={a.id}
                          as="button"
                          aria-label={`Ver melhoria ${i + 1} de ${total}`}
                          aria-current={i === idx}
                          onClick={() => irPara(i)}
                          w={i === idx ? '14px' : '6px'}
                          h="6px"
                          p={0}
                          borderRadius="full"
                          bg={i === idx ? '#6366F1' : 'rgba(99, 102, 241, 0.28)'}
                          transition="width 0.24s ease, background 0.24s ease"
                          _hover={{ bg: i === idx ? '#6366F1' : 'rgba(99, 102, 241, 0.55)' }}
                        />
                      ))}
                      <IconButton
                        aria-label="Próxima melhoria"
                        icon={<CaretRight size={12} weight="bold" />}
                        size="xs"
                        variant="ghost"
                        borderRadius="full"
                        onClick={() => irPara((idx + 1) % total)}
                      />
                      {pausado && (
                        <Text fontSize="10px" color="#94A3B8" ml={1}>
                          navegação manual
                        </Text>
                      )}
                    </Flex>
                  )}
                </Box>

                {/* Recolhe a leva inteira, não o aviso da vez: quem clica no X
                  está dizendo "já vi isto", e fechar um por um obrigaria a
                  repetir o gesto oito vezes. O card vira o acionador, então
                  nada some de vez. */}
                <IconButton
                  aria-label="Recolher as melhorias"
                  icon={<X size={14} weight="bold" />}
                  size="xs"
                  variant="ghost"
                  borderRadius="full"
                  color="#94A3B8"
                  flexShrink={0}
                  onClick={recolher}
                />
              </Flex>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
